/**
 * Local storage helpers for POS data
 */

// ─── Tax Rates ───────────────────────────────────────────────────────────────

export interface TaxRate {
  id: string;
  name: string;   // e.g. "Standard", "Food", "Liquor"
  rate: number;   // percentage, e.g. 8.5
}

// ─── Coupons ─────────────────────────────────────────────────────────────────

export interface Coupon {
  id: string;
  code: string;             // e.g. "SUMMER10"
  type: 'percent' | 'flat'; // percent off or flat dollar off
  value: number;            // e.g. 10 for 10% or $10 off
  active: boolean;
  description?: string;
}

const COUPONS_KEY = 'bch-pos:coupons';

export function loadCoupons(): Coupon[] {
  try {
    const raw = localStorage.getItem(COUPONS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Coupon[];
  } catch { return []; }
}

export function saveCoupons(coupons: Coupon[]): void {
  try { localStorage.setItem(COUPONS_KEY, JSON.stringify(coupons)); }
  catch (e) { console.warn('Failed to save coupons', e); }
}

export function upsertCoupon(coupon: Coupon): void {
  const list = loadCoupons();
  const idx = list.findIndex((c) => c.id === coupon.id);
  if (idx >= 0) list[idx] = coupon; else list.push(coupon);
  saveCoupons(list);
}

export function deleteCoupon(id: string): void {
  saveCoupons(loadCoupons().filter((c) => c.id !== id));
}

// ─── Products / Menu ─────────────────────────────────────────────────────────

export interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  imageDataUrl?: string;
  description?: string;
  available: boolean;
  taxRateId?: string; // references a TaxRate id; null = use default rate
}

const PRODUCTS_KEY = 'bch-pos:products';

export function loadProducts(): Product[] {
  try {
    const raw = localStorage.getItem(PRODUCTS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Product[];
  } catch { return []; }
}

export function saveProducts(products: Product[]): void {
  try { localStorage.setItem(PRODUCTS_KEY, JSON.stringify(products)); }
  catch (e) { console.warn('Failed to save products', e); }
}

export function upsertProduct(product: Product): void {
  const products = loadProducts();
  const idx = products.findIndex((p) => p.id === product.id);
  if (idx >= 0) products[idx] = product; else products.push(product);
  saveProducts(products);
}

export function deleteProduct(id: string): void {
  saveProducts(loadProducts().filter((p) => p.id !== id));
}

// ─── Transactions ─────────────────────────────────────────────────────────────

const TRANSACTIONS_KEY = 'bch-pos:transactions';

export interface TransactionLineItem {
  name: string;
  qty: number;
  price: number;
}

export interface TaxLine {
  name: string;
  rate: number;
  amount: number;
}

export interface Transaction {
  id: string;
  subtotal: number;       // pre-discount, pre-tax, pre-tip
  discountAmount: number; // fiat discount applied
  discountLabel: string;  // e.g. "10% off" or "SUMMER10"
  taxAmount: number;      // total fiat tax (sum of all tax lines)
  taxLines: TaxLine[];    // breakdown per tax rate
  tipAmount: number;
  fiatAmount: number;     // grand total
  bchAmount: number;
  currency: string;
  // Settlement asset. Absent/undefined = BCH (back-compat for existing records).
  asset?: 'BCH' | 'PUSD';
  tokenAmount?: number;   // stablecoin amount in display units (e.g. 5.00 PUSD)
  tokenSymbol?: string;   // e.g. "PUSD"
  taxRate: number;        // legacy / default rate
  taxName: string;        // legacy / default name
  note?: string;
  txid: string;
  address: string;
  timestamp: number;
  label?: string;
  items?: TransactionLineItem[];
  refunded?: boolean;     // marked as refunded (BCH refund is on-chain, this is just a flag)
  refundedAt?: number;    // timestamp of when it was marked refunded
  refundNote?: string;    // optional reason
}

export function markTransactionRefunded(id: string, note?: string): void {
  try {
    const txs = loadTransactions();
    const idx = txs.findIndex((t) => t.id === id);
    if (idx < 0) return;
    txs[idx] = { ...txs[idx], refunded: true, refundedAt: Date.now(), refundNote: note };
    localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(txs));
  } catch (e) { console.warn('Failed to mark refund', e); }
}

export function unmarkTransactionRefunded(id: string): void {
  try {
    const txs = loadTransactions();
    const idx = txs.findIndex((t) => t.id === id);
    if (idx < 0) return;
    txs[idx] = { ...txs[idx], refunded: false, refundedAt: undefined, refundNote: undefined };
    localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(txs));
  } catch (e) { console.warn('Failed to unmark refund', e); }
}

// ─── Shifts ───────────────────────────────────────────────────────────────────

export interface Shift {
  id: string;
  openedAt: number;
  closedAt?: number;
  note?: string;         // e.g. staff name
}

const SHIFTS_KEY = 'bch-pos:shifts';

export function loadShifts(): Shift[] {
  try {
    const raw = localStorage.getItem(SHIFTS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Shift[];
  } catch { return []; }
}

export function saveShifts(shifts: Shift[]): void {
  try { localStorage.setItem(SHIFTS_KEY, JSON.stringify(shifts.slice(0, 100))); }
  catch (e) { console.warn('Failed to save shifts', e); }
}

export function openShift(note?: string): Shift {
  const shift: Shift = { id: crypto.randomUUID(), openedAt: Date.now(), note };
  const shifts = loadShifts();
  shifts.unshift(shift);
  saveShifts(shifts);
  return shift;
}

export function closeShift(id: string): void {
  const shifts = loadShifts();
  const idx = shifts.findIndex((s) => s.id === id);
  if (idx >= 0) shifts[idx] = { ...shifts[idx], closedAt: Date.now() };
  saveShifts(shifts);
}

export function getActiveShift(): Shift | null {
  return loadShifts().find((s) => !s.closedAt) ?? null;
}

// ─── Settings ────────────────────────────────────────────────────────────────

export interface POSSettings {
  bchAddress: string;
  currency: string;
  merchantName: string;
  merchantTagline?: string; // shown under name on checkout/success/receipt
  logoDataUrl?: string;
  brandColor: string;       // hex color for headers and accents, default #0AC18E
  taxRate: number;
  taxName: string;
  taxRates: TaxRate[]; // named tax rates for products
  skipReceipt: boolean; // auto-reset immediately after payment, skip receipt screen
  dailySummary: boolean; // send a daily notification with yesterday's sales totals
  enableTipping: boolean; // show tip screen before payment
  acceptPUSD: boolean; // allow accepting ParyonUSD (PUSD) stablecoin at checkout
  // ── Merchant Discovery (Nostr) ──
  publishActivity: boolean;      // publish activity heartbeats to Nostr
  merchantCategory?: string;     // e.g. "cafe", "restaurant", "retail"
  merchantWebsite?: string;      // public website URL
  merchantGeohash?: string;      // coarse geohash for map discovery (opt-in)
  merchantLocationName?: string; // human-readable location label, e.g. "Austin, TX"
}

const SETTINGS_KEY = 'bch-pos:settings';

export const DEFAULT_SETTINGS: POSSettings = {
  bchAddress: '',
  currency: 'USD',
  merchantName: 'My Store',
  merchantTagline: '',
  logoDataUrl: undefined,
  brandColor: '#0AC18E',
  taxRate: 0,
  taxName: 'Tax',
  taxRates: [],
  skipReceipt: false,
  dailySummary: false,
  enableTipping: true,
  acceptPUSD: false,
  publishActivity: false,
  merchantCategory: undefined,
  merchantWebsite: undefined,
  merchantGeohash: undefined,
  merchantLocationName: undefined,
};

export function loadTransactions(): Transaction[] {
  try {
    const raw = localStorage.getItem(TRANSACTIONS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Transaction[];
  } catch { return []; }
}

export function saveTransaction(tx: Transaction): void {
  try {
    const txs = loadTransactions();
    txs.unshift(tx);
    localStorage.setItem(TRANSACTIONS_KEY, JSON.stringify(txs.slice(0, 500)));
  } catch (e) { console.warn('Failed to save transaction', e); }
}

export function loadSettings(): POSSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } as POSSettings;
  } catch { return DEFAULT_SETTINGS; }
}

export function saveSettings(settings: POSSettings): void {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); }
  catch (e) { console.warn('Failed to save settings', e); }
}
